/**
 * Notification Permission Banner
 *
 * Shows a banner prompting user to enable push notifications
 * Appears when notifications are not enabled
 * Supports both web and native platforms (Capacitor)
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Bell } from 'lucide-react';
import {
  isNotificationSupported,
  getNotificationPermission,
  requestNotificationPermission,
  getFCMToken,
  isNativePlatform,
  initializeNativePush,
  ensureFCMTokenSaved
} from '@/services/notificationService';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/lib/toast';

export function NotificationPermissionBanner() {
  const { t } = useTranslation();
  const { firebaseUser } = useAuth();
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isDismissed, setIsDismissed] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);

  useEffect(() => {
    // Check if notifications are supported
    if (!isNotificationSupported()) {
      setIsDismissed(true);
      return;
    }

    // For native platforms, automatically request permissions on mount
    // This triggers the native OS permission dialog
    if (isNativePlatform()) {
      // Check if already dismissed or if user is authenticated
      const dismissed = localStorage.getItem('notification-banner-dismissed');
      if (dismissed === 'true') {
        setIsDismissed(true);
        return;
      }

      // Auto-request permissions for native platforms when user is authenticated
      if (firebaseUser && !firebaseUser.isAnonymous) {
        (async () => {
          try {
            console.log('[NotificationBanner] Auto-requesting native push permissions...');
            console.log('[NotificationBanner] User ID:', firebaseUser.uid);

            // First try to initialize push (will request permission if needed)
            const token = await initializeNativePush();

            if (token) {
              console.log('[NotificationBanner] ✅ Native push permissions granted automatically');
              setPermission('granted');
              setIsDismissed(true);
              localStorage.setItem('notification-banner-dismissed', 'true');
            } else {
              // Token might not have been saved - try to ensure it's saved
              console.log('[NotificationBanner] No token returned, trying to ensure FCM token is saved...');
              const ensured = await ensureFCMTokenSaved();
              if (ensured) {
                console.log('[NotificationBanner] ✅ FCM token ensured and saved');
                setPermission('granted');
                setIsDismissed(true);
                localStorage.setItem('notification-banner-dismissed', 'true');
              }
            }
          } catch (error) {
            console.error('[NotificationBanner] Error auto-requesting native permissions:', error);
          }
        })();
      }

      // Don't show the banner on native - the OS dialog is enough
      setIsDismissed(true);
      return;
    }

    // Check current permission status (web only)
    const currentPermission = getNotificationPermission();
    setPermission(currentPermission);

    // Check if user dismissed before
    const dismissed = localStorage.getItem('notification-banner-dismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
    }
  }, [firebaseUser]);

  const handleRequestPermission = async () => {
    if (!firebaseUser || firebaseUser.isAnonymous) {
      toast.error(t('notifications.permissionRequired'));
      return;
    }

    setIsRequesting(true);
    try {
      // Handle native platforms differently
      if (isNativePlatform()) {
        console.log('Requesting native push notification permission...');
        const token = await initializeNativePush();
        if (token) {
          setPermission('granted');
          setIsDismissed(true);
          localStorage.setItem('notification-banner-dismissed', 'true');
          toast.success(t('notifications.permission.enabled') || 'Notificaciones activadas');
          console.log('Native notifications enabled successfully');
        } else {
          toast.error(t('notifications.permission.error') || 'Error al activar notificaciones');
        }
        setIsRequesting(false);
        return;
      }

      // Handle web platforms
      const newPermission = await requestNotificationPermission();
      setPermission(newPermission);

      if (newPermission === 'granted') {
        // Get FCM token
        await getFCMToken(firebaseUser.uid);
        console.log('Notifications enabled successfully');
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem('notification-banner-dismissed', 'true');
  };

  // Don't show banner if:
  // - User dismissed it
  // - Permission already granted
  // - Permission denied
  // - User is not logged in or is anonymous
  if (
    isDismissed ||
    permission === 'granted' ||
    permission === 'denied' ||
    !firebaseUser ||
    firebaseUser.isAnonymous
  ) {
    return null;
  }

  return (
    <div className="fixed top-14 lg:top-0 lg:left-64 left-0 right-0 z-[90] bg-info/10 border-b border-info/30 shadow-md">
      <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex-1 flex items-center min-w-0">
            <span className="flex-shrink-0 flex p-2 rounded-lg bg-info/10">
              <Bell className="h-5 w-5 text-info" />
            </span>
            <p className="ml-3 font-medium text-info text-sm truncate">
              <span className="md:hidden">
                {t('notifications.permission.activate')}
              </span>
              <span className="hidden md:inline">
                {t('notifications.permission.description')}
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleRequestPermission}
              disabled={isRequesting}
              className="px-3 py-1.5 bg-info text-white rounded-lg hover:bg-info/90 disabled:bg-info disabled:cursor-not-allowed font-medium transition text-sm whitespace-nowrap"
            >
              {isRequesting ? t('notifications.permission.enabling') : t('notifications.permission.enable')}
            </button>
            <button
              onClick={handleDismiss}
              className="p-1.5 rounded-lg hover:bg-info/20 transition"
            >
              <X className="h-5 w-5 text-info" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
